import type { TicketListItem } from '@/types/api'
import { parseApiDateUtc } from '@/lib/api-date'
import type { Tone } from '@/lib/tone'
import { getUserTimeZone } from '@/lib/user-timezone'

export { parseApiDateUtc }

const STATUS_MAP: Record<string, Tone> = {
  new: 'info',
  open: 'info',
  pending: 'warning',
  on_hold: 'muted',
  in_progress: 'neutral',
  emergency: 'urgent',
  resolved: 'success',
  closed: 'muted',
  spam: 'muted',
}

const PRIORITY_MAP: Record<string, Tone> = {
  low: 'muted',
  normal: 'neutral',
  high: 'warning',
  urgent: 'urgent',
}

export function statusTone(status: string | null | undefined): Tone {
  const s = String(status ?? '')
  return STATUS_MAP[s] ?? 'neutral'
}

export function priorityTone(priority: string | null | undefined): Tone {
  const p = String(priority ?? 'normal').toLowerCase()
  return PRIORITY_MAP[p] ?? 'neutral'
}

export function statusLabel(status: string | null | undefined): string {
  const labels: Record<string, string> = {
    new: 'Новый',
    open: 'Открыт',
    pending: 'Ожидает',
    on_hold: 'На паузе',
    in_progress: 'В работе',
    emergency: 'Авария',
    resolved: 'Решён',
    closed: 'Закрыт',
    spam: 'Спам',
  }
  const s = String(status ?? '')
  return labels[s] || s || '—'
}

export function priorityLabel(priority: string | null | undefined): string {
  const p = String(priority ?? 'normal').toLowerCase()
  const labels: Record<string, string> = {
    low: 'Низкий',
    normal: 'Обычный',
    high: 'Высокий',
    urgent: 'Срочно',
  }
  return labels[p] ?? String(priority ?? '')
}

/** Компактная буква для колонки таблицы (как узкий индикатор приоритета) */
export function priorityShortLabel(priority: string | null | undefined): string {
  const p = String(priority ?? 'normal').toLowerCase()
  const map: Record<string, string> = {
    low: 'Н',
    normal: 'О',
    high: 'В',
    urgent: 'С',
  }
  const tail = String(priority ?? '').slice(0, 1).toUpperCase()
  return map[p] ?? (tail || '?')
}

export { getUserTimeZone } from '@/lib/user-timezone'

/** Не показывать «N мин/ch/дн назад», если с момента события прошло больше 4 суток. */
const RELATIVE_MAX_MS = 4 * 24 * 60 * 60 * 1000

/**
 * Относительное время на русском: «только что», «5 мин назад», «2 ч назад», «3 дн назад».
 * Не больше 4 суток — иначе `null` (остаётся только абсолютная дата).
 */
export function formatRelativeAgoRu(iso: string | null | undefined, nowMs = Date.now()): string | null {
  if (!iso) return null
  const instant = parseApiDateUtc(iso)
  if (!instant) return null
  const t = instant.getTime()
  const diff = nowMs - t
  if (diff < 0) return null
  if (diff > RELATIVE_MAX_MS) return null
  const sec = Math.floor(diff / 1000)
  if (sec < 60) return 'только что'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min} мин назад`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h} ч назад`
  const days = Math.floor(h / 24)
  if (days >= 1 && days <= 4) return `${days} дн назад`
  return null
}

/**
 * Компактное относительное время для таблиц списков: «сейч», «12м», «1ч», «3д».
 * Тот же порог 4 суток, что у `formatRelativeAgoRu`.
 */
export function formatRelativeAgoCompact(iso: string | null | undefined, nowMs = Date.now()): string | null {
  if (!iso) return null
  const instant = parseApiDateUtc(iso)
  if (!instant) return null
  const t = instant.getTime()
  const diff = nowMs - t
  if (diff < 0) return null
  if (diff > RELATIVE_MAX_MS) return null
  const sec = Math.floor(diff / 1000)
  if (sec < 60) return 'сейч'
  const min = Math.floor(sec / 60)
  if (min < 60) return `${min}м`
  const h = Math.floor(min / 60)
  if (h < 24) return `${h}ч`
  const days = Math.floor(h / 24)
  if (days >= 1 && days <= 4) return `${days}д`
  return null
}

/** @deprecated Используйте formatRelativeAgoRu */
export const relativeTimeHint = formatRelativeAgoRu

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

const MONTHS_SHORT_RU = [
  'янв',
  'фев',
  'мар',
  'апр',
  'май',
  'июн',
  'июл',
  'авг',
  'сен',
  'окт',
  'ноя',
  'дек',
] as const

/** Календарные часы:минуты в TZ пользователя */
function wallClockInTz(iso: string, tz: string): { y: number; mo: number; d: number; h: number; mi: number } | null {
  const instant = parseApiDateUtc(iso)
  if (!instant) return null
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(instant)
  const o: Record<string, string> = {}
  for (const p of parts) {
    if (p.type !== 'literal') o[p.type] = p.value
  }
  const y = parseInt(o.year || '', 10)
  const mo = parseInt(o.month || '', 10)
  const d = parseInt(o.day || '', 10)
  const h = parseInt(o.hour || '', 10)
  const mi = parseInt(o.minute || '', 10)
  if ([y, mo, d, h, mi].some((x) => Number.isNaN(x))) return null
  return { y, mo, d, h, mi }
}

export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return '—'
  const tz = getUserTimeZone()
  const w = wallClockInTz(iso, tz)
  if (!w) return '—'
  const mon = MONTHS_SHORT_RU[w.mo - 1] ?? ''
  return `${pad2(w.d)} ${mon} ${pad2(w.h)}:${pad2(w.mi)}`
}

/** Как в классике: `02.03.2026, 11:38` — в часовом поясе пользователя */
export function formatDateTimeDot(iso: string | null | undefined): string {
  if (!iso) return '—'
  const tz = getUserTimeZone()
  const w = wallClockInTz(iso, tz)
  if (!w) return '—'
  return `${pad2(w.d)}.${pad2(w.mo)}.${w.y}, ${pad2(w.h)}:${pad2(w.mi)}`
}

/** Компактно для таблицы: `22 мар 11:26` — в TZ пользователя */
export function formatWhenCompact(iso: string | null | undefined): string {
  if (!iso) return '—'
  const tz = getUserTimeZone()
  const w = wallClockInTz(iso, tz)
  if (!w) return '—'
  const mon = MONTHS_SHORT_RU[w.mo - 1] ?? ''
  return `${w.d} ${mon} ${pad2(w.h)}:${pad2(w.mi)}`
}

/** Для списков: `08.04 14:30` в текущем году или `08.04.25 14:30` — в TZ пользователя */
export function formatWhenNumericShort(iso: string | null | undefined): string {
  if (!iso) return '—'
  const tz = getUserTimeZone()
  const w = wallClockInTz(iso, tz)
  if (!w) return '—'
  const now = new Date()
  const nw = wallClockInTz(now.toISOString(), tz)
  const sameYear = nw && w.y === nw.y
  const dateStr = sameYear
    ? `${pad2(w.d)}.${pad2(w.mo)}`
    : `${pad2(w.d)}.${pad2(w.mo)}.${String(w.y).slice(-2)}`
  return `${dateStr} ${pad2(w.h)}:${pad2(w.mi)}`
}

function appendRelativeCompact(iso: string | null | undefined, base: string): string {
  const rel = formatRelativeAgoCompact(iso)
  if (rel) return `${base} · ${rel}`
  return base
}

/** Дата/время последнего события в списке — короткая дата + «1ч» / «2д» (до 4 суток). */
export function formatTicketListUpdatedAt(t: TicketListItem): string {
  const raw = t.last_update_at
  const hasTime = raw != null && String(raw).trim() !== ''
  const iso = hasTime ? raw : (t.updated_at || t.created_at)
  return appendRelativeCompact(iso, formatWhenNumericShort(iso))
}

/** Колонка «Создан» в списках: `08.04 14:30 · 2д` и т.п. */
export function formatCreatedAtTableCell(iso: string | null | undefined): string {
  if (!iso) return '—'
  return appendRelativeCompact(iso, formatWhenNumericShort(iso))
}

/** Подпись «кем обновлено» для колонки «Обновлён». */
export function lastUpdateSenderLabel(t: TicketListItem): string {
  const by = t.last_update_by
  const hasWho = by === 'customer' || by === 'operator' || by === 'system'
  if (!hasWho) return '—'
  if (by === 'system') {
    return (t.last_update_sender_name?.trim()) || 'Системное'
  }
  if (t.last_update_sender_name?.trim()) return t.last_update_sender_name.trim()
  if (by === 'customer') {
    return (
      (t.customer_name || t.customer_email || 'Клиент').trim() || 'Клиент'
    )
  }
  return 'Оператор'
}

export function customerLine(t: TicketListItem): string {
  if (t.customer_name && t.customer_email) {
    return `${t.customer_name} · ${t.customer_email}`
  }
  return t.customer_name || t.customer_email || '—'
}

/**
 * Сегмент пути для роута `/tickets/:ticketRef` — как в классике: `uboost-tv-53455`.
 * Без slug в данных остаётся UUID.
 */
export function ticketPathSegmentFromListItem(t: {
  id: string
  ticket_number: number
  project_slug?: string | null
}): string {
  const slug = (t.project_slug || '').trim()
  if (slug && Number.isFinite(t.ticket_number)) {
    return `${slug}-${t.ticket_number}`
  }
  return t.id
}

/** Публичный номер вида NARUZHU-123 (slug проекта в верхнем регистре). */
export function ticketPublicId(
  projectSlug: string | null | undefined,
  ticketNumber: number,
): string {
  const s = (projectSlug || '').trim()
  if (s) return `${s.toUpperCase()}-${ticketNumber}`
  return `#${ticketNumber}`
}

/** Заголовок вкладки: «SLUG-NUM Тема…». */
export function ticketWorkspaceTabTitle(
  projectSlug: string | null | undefined,
  ticketNumber: number,
  subject: string | null | undefined,
  maxLen = 56,
): string {
  const id = ticketPublicId(projectSlug, ticketNumber)
  const subj = (subject || '').trim() || '—'
  const budget = maxLen - id.length - 1
  if (budget < 6) return id
  const truncated = subj.length > budget ? `${subj.slice(0, budget - 1)}…` : subj
  return `${id} ${truncated}`
}
